import React, { ComponentProps } from 'react';
import { Direction, TabPanel, Tabs } from '../../../components/molecules/Tabs';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { PageInstantReactionListViewProps, ReactionEntry, ReactionListTab } from '../PageInstantReaction.types';
import { PageInstantReactionIcon, PageInstantReactionIconModifier } from '../partials/PageInstantReactionIcon';
import { PaddingContainerDiv } from '../../../components/atoms/PaddingContainer/PaddingContainer';
import { ContentList, ContentListItem } from '../../../_lib/components/ContentList';
import { PersonInformation } from '../../../components/molecules/PersonInformation';
import ModalDialogAdditionalScrollContainer from '../../../components/organisms/ModalDialog/ModalDialogAdditionalScrollContainer';

const tabId = (tab: ReactionListTab): string => `page-instant-reaction-tab-${tab}`;

function PageInstantReactionList(props: PageInstantReactionListViewProps) {
  const { t } = useSonTranslation('PageInstantReaction');
  const tabLabel = (tab: ReactionListTab): string => t(`reactions.${tab}`);
  const tabOrder = Object.entries(props) as [ReactionListTab, ReactionEntry[]][];

  return (
    <Tabs initialActiveTab={tabId('all')} direction={Direction.HORIZONTAL} label={t('evaluation.tabList')}>
      {tabOrder.map(([tab, reactions]) => (
        <ModalDialogAdditionalScrollContainer key={tabId(tab)}>
          <TabPanel
            buttonLabel={
              tab === 'all' ? (
                `${tabLabel(tab)}: ${reactions.length}`
              ) : (
                <>
                  <PageInstantReactionIcon reaction={tab} count={reactions.length} />
                  <span className="invisible">
                    {`${tabLabel(tab)}: ${reactions.length} ${t('evaluation.reaction')}`}
                  </span>
                </>
              )
            }
            tooltip={tab !== 'all' ? `${tabLabel(tab)}: ${reactions.length}` : undefined}
            id={tabId(tab)}
          >
            {reactions.length === 0 ? (
              <PaddingContainerDiv>
                <p className="font-regular-15-19">{t('list.noData')}</p>
              </PaddingContainerDiv>
            ) : (
              <ContentList>
                {reactions.map((entry, index) => (
                  <ContentListItem key={entry.author.profileLink?.url ?? index}>
                    <div className="page-instant-reaction__entry">
                      <PersonInformation
                        deletedUser={entry.author.deletedUser}
                        infoCardUrl={entry.author.personInfoCardUrl ?? undefined}
                        profileImageUrl={entry.author.profileImageUrl ?? undefined}
                        profileLink={entry.author.profileLink ?? undefined}
                        profileName={entry.author.profileName}
                      />
                      <div className="page-instant-reaction__entry-reaction">
                        <PageInstantReactionIcon
                          reaction={entry.instantReaction}
                          reactionInformation
                          modifier={PageInstantReactionIconModifier.SMALL}
                        />
                      </div>
                    </div>
                  </ContentListItem>
                ))}
              </ContentList>
            )}
          </TabPanel>
        </ModalDialogAdditionalScrollContainer>
      ))}
    </Tabs>
  );
}

export const PageInstantReactionListView = asFetchableView(PageInstantReactionList);
export type PageInstantReactionListView = ComponentProps<typeof PageInstantReactionListView>;
